import { useMemo, useState } from 'react';
import * as Sentry from '@sentry/react';
import { authClient } from '../../utils/amplifyClient';
import { useUserContext } from '../../contexts/UserContext';
import { useRegionAndLocationContext } from '../../contexts/RegionAndLocationContext';
import { useEquipmentContext } from '../../contexts/EquipmentContext';
import { RegionCard } from './RegionCard';
import { AddRegionModal } from './AddRegionModal';
import { AddDistrictModal } from './AddDistrictModal';
import { EditLocationModal } from './EditLocationModal';
import { DeleteRegionModal } from './DeleteRegionModal';
import { DeleteDistrictModal } from './DeleteDistrictModal';
import { DeleteLocationModal } from './DeleteLocationModal';
import { District, Location, RegionalDivision } from '../../types/schema';
import { tGroupedRegionWithDistricts, tGroupedDistrict } from '../../types';
import {
    createRegionalDivision,
    createLocation,
    deleteRegionalDivision,
    deleteLocation as deleteLocationMutation,
    updateSingleUnit as updateSingleUnitMutation,
} from '../../graphql/mutations';
import '../../css/RegionsPage.css';
import { updateServiceCenter } from '../../graphql/companyResourceQueries';
import { useCompanyServiceCenters } from '../../hooks/useCompanyServiceCenters';
import { Btn, IconPlus, PageHeader, TEIQspinner, type SelectOption } from '../ui';
import { buildInterimLocationDescription } from '../../utils/regionLocationHierarchy';
import {
    activeServiceCenters,
    cleanIdList,
    locationIdsForAssignment,
    serviceCenterIdForLocation,
} from '../../utils/serviceCenterUtils';

export const RegionsPage = () => {
    const { company } = useUserContext();
    const {
        companyGroupedData,
        companyLocations,
        loading,
        refreshRegionAndLocation,
        addRegion,
        addDistrict,
        addLocation,
    } = useRegionAndLocationContext();
    const { companyUnits } = useEquipmentContext();

    const [searchQuery, setSearchQuery] = useState('');
    const [expandedRegions, setExpandedRegions] = useState<Set<string>>(new Set());

    // Modal state
    const [regionModal, setRegionModal] = useState<{ open: boolean; region?: RegionalDivision }>({ open: false });
    const [districtModal, setDistrictModal] = useState<{
        open: boolean;
        regionId?: string;
        regionName?: string;
        district?: District;
    }>({ open: false });
    const [locationModal, setLocationModal] = useState<{ open: boolean; location?: Location }>({ open: false });
    const [deleteRegionModal, setDeleteRegionModal] = useState<{ open: boolean; region?: tGroupedRegionWithDistricts }>(
        { open: false },
    );
    const [deleteDistrictModal, setDeleteDistrictModal] = useState<{ open: boolean; district?: tGroupedDistrict }>({
        open: false,
    });
    const [deleteLocationModal, setDeleteLocationModal] = useState<{ open: boolean; location?: Location }>({
        open: false,
    });

    const companyId = company?.id || '';
    const { serviceCenters, reloadServiceCenters } = useCompanyServiceCenters(companyId);
    const serviceCenterOptions = useMemo<SelectOption[]>(
        () => [
            { value: '', label: 'None' },
            ...activeServiceCenters(serviceCenters).map((center) => ({
                value: center.id,
                label: center.name || 'Service center',
            })),
        ],
        [serviceCenters],
    );
    const assignedServiceCenterId = (location: Location) =>
        serviceCenterIdForLocation(location, serviceCenters) ?? '';

    // Stats — districts are top-level Location stand-ins; locations are nested under them
    const totalRegions = companyGroupedData.length;
    const totalDistricts = companyGroupedData.reduce((sum, r) => sum + r.districts.length, 0);
    const totalLocations = companyGroupedData.reduce(
        (sum, r) => sum + r.districts.reduce((inner, d) => inner + d.locations.length, 0),
        0,
    );
    const regionIdSet = useMemo(() => new Set(companyGroupedData.map((r) => r.id)), [companyGroupedData]);
    const unassignedLocations = useMemo(
        () =>
            companyLocations.filter(
                (loc) => !loc.regionalDivisionId || !regionIdSet.has(loc.regionalDivisionId),
            ),
        [companyLocations, regionIdSet],
    );

    // Get equipment count for a location
    const getLocationEquipmentCount = (locationId: string): number => {
        return companyUnits.filter((u) => u.locationId === locationId).length;
    };

    // Search filter
    const filteredData = useMemo(() => {
        if (!searchQuery.trim()) return companyGroupedData;
        const q = searchQuery.toLowerCase();
        return companyGroupedData.filter((region) => {
            if (region.name.toLowerCase().includes(q)) return true;
            return region.districts.some(
                (d) => d.name.toLowerCase().includes(q) || d.locations.some((l) => l.name.toLowerCase().includes(q)),
            );
        });
    }, [companyGroupedData, searchQuery]);

    // Toggle region expand/collapse
    const toggleRegion = (regionId: string) => {
        setExpandedRegions((prev) => {
            const next = new Set(prev);
            if (next.has(regionId)) next.delete(regionId);
            else next.add(regionId);
            return next;
        });
    };

    // ─── Handlers ───

    const handleCreateRegion = async (name: string, description: string) => {
        try {
            const result = await authClient.graphql({
                query: createRegionalDivision,
                variables: { input: { name, description, companyId } },
            });
            addRegion(result.data.createRegionalDivision as RegionalDivision);
            setRegionModal({ open: false });
            await refreshRegionAndLocation();
        } catch (error) {
            console.error('Error creating region:', error);
            Sentry.captureException(error);
        }
    };

    const handleCreateDistrict = async (name: string, description: string, regionalDivisionId: string) => {
        try {
            const result = await authClient.graphql({
                query: createLocation,
                variables: { input: { name, description, companyId, regionalDivisionId } },
            });
            addDistrict(result.data.createLocation as unknown as District);
            setDistrictModal({ open: false });
            await refreshRegionAndLocation();
        } catch (error) {
            console.error('Error creating district:', error);
            Sentry.captureException(error);
        }
    };

    // Gen2 has no districtId — nest under the district via interim description encoding.
    const handleCreateLocation = async (name: string, districtId: string, regionalDivisionId: string) => {
        try {
            const result = await authClient.graphql({
                query: createLocation,
                variables: {
                    input: {
                        name,
                        companyId,
                        regionalDivisionId,
                        description: buildInterimLocationDescription(districtId),
                    },
                },
            });
            addLocation(result.data.createLocation as Location);
            await refreshRegionAndLocation();
        } catch (error) {
            console.error('Error creating location:', error);
            Sentry.captureException(error);
        }
    };

    /** Clear locationId on any SingleUnit records that reference a deleted location */
    const clearLocationFromUnits = async (locationId: string) => {
        try {
            const { data } = await authClient.models.SingleUnit.list({ limit: 10000 });
            const affectedUnits = data.filter((u) => u.locationId === locationId);
            await Promise.allSettled(
                affectedUnits.map((unit) =>
                    authClient.graphql({
                        query: updateSingleUnitMutation,
                        variables: { input: { id: unit.id, locationId: null } },
                    })
                )
            );
            if (affectedUnits.length > 0) {
                console.log(`Cleared locationId on ${affectedUnits.length} unit(s) referencing deleted location ${locationId}`);
            }
        } catch (error) {
            console.error('Error clearing locationId from units:', error);
            Sentry.captureException(error);
        }
    };

    const handleDeleteRegion = async (regionId: string) => {
        try {
            // Delete all locations in all districts
            const region = companyGroupedData.find((r) => r.id === regionId);
            if (!region) return;

            for (const district of region.districts) {
                for (const location of district.locations) {
                    await clearLocationFromUnits(location.id);
                    await authClient.graphql({ query: deleteLocationMutation, variables: { input: { id: location.id } } });
                }
                await clearLocationFromUnits(district.id);
                await authClient.graphql({ query: deleteLocationMutation, variables: { input: { id: district.id } } });
            }
            await authClient.graphql({ query: deleteRegionalDivision, variables: { input: { id: regionId } } });
            setDeleteRegionModal({ open: false });
            await refreshRegionAndLocation();
        } catch (error) {
            console.error('Error deleting region:', error);
            Sentry.captureException(error);
        }
    };

    const handleDeleteDistrict = async (districtId: string) => {
        try {
            const district = companyGroupedData.flatMap((r) => r.districts).find((d) => d.id === districtId);
            if (district) {
                for (const location of district.locations) {
                    await clearLocationFromUnits(location.id);
                    await authClient.graphql({ query: deleteLocationMutation, variables: { input: { id: location.id } } });
                }
            }
            await clearLocationFromUnits(districtId);
            await authClient.graphql({ query: deleteLocationMutation, variables: { input: { id: districtId } } });
            setDeleteDistrictModal({ open: false });
            await refreshRegionAndLocation();
        } catch (error) {
            console.error('Error deleting district:', error);
            Sentry.captureException(error);
        }
    };

    const handleAssignServiceCenter = async (location: Location, serviceCenterId: string) => {
        const selectedCenterId = serviceCenterId || null;
        if ((serviceCenterIdForLocation(location, serviceCenters) ?? null) === selectedCenterId) return;
        try {
            const updates = serviceCenters.filter((center) => {
                const nextIds = locationIdsForAssignment(center, location.id, selectedCenterId);
                return nextIds.join('\0') !== cleanIdList(center.locationIds).join('\0');
            });
            await Promise.all(
                updates.map((center) =>
                    updateServiceCenter({
                        id: center.id,
                        companyId: center.companyId,
                        name: center.name,
                        address: center.address,
                        phoneNumber: center.phoneNumber,
                        faxNumber: center.faxNumber,
                        repName: center.repName,
                        repPhoneNumber: center.repPhoneNumber,
                        regionIds: cleanIdList(center.regionIds),
                        locationIds: locationIdsForAssignment(center, location.id, selectedCenterId),
                        archived: center.archived,
                    }),
                ),
            );
            reloadServiceCenters();
        } catch (error) {
            console.error('Error assigning service center:', error);
            Sentry.captureException(error);
        }
    };

    const handleDeleteLocation = async (locationId: string) => {
        try {
            await clearLocationFromUnits(locationId);
            await authClient.graphql({ query: deleteLocationMutation, variables: { input: { id: locationId } } });
            setDeleteLocationModal({ open: false });
            await refreshRegionAndLocation();
        } catch (error) {
            console.error('Error deleting location:', error);
            Sentry.captureException(error);
        }
    };

    if (loading) {
        return (
            <div className="regions-loading">
                <TEIQspinner variant="lg" label="Loading regions" />
            </div>
        );
    }

    return (
        <div className="page-container regions-page">
            {/* Header */}
            <PageHeader
                title="Regional Divisions"
                signalLine="Organize your operation into Regions, Districts, and Locations. Equipment is assigned to Locations from the Equipment module."
                actions={
                    <Btn
                        variant="primary"
                        buttonClasses="btn-add"
                        buttonText="Add Region"
                        buttonIcon={<IconPlus iconClasses="btn-add-icon" />}
                        buttonOnClick={() => setRegionModal({ open: true })}
                    />
                }
            />
            {/* Search & Stats */}
            <div className="regions-toolbar">
                <div className="regions-search">
                    <input
                        type="text"
                        placeholder="Search regions, districts, locations..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>
                <div className="regions-stats">
                    <b>{totalRegions}</b> region{totalRegions !== 1 ? 's' : ''} ·{' '}
                    <b>{totalDistricts}</b> district{totalDistricts !== 1 ? 's' : ''} ·{' '}
                    <b>{totalLocations}</b> location{totalLocations !== 1 ? 's' : ''}
                    {unassignedLocations.length > 0 && (
                        <>
                            {' '}
                            · <b>{unassignedLocations.length}</b> unassigned
                        </>
                    )}
                </div>
            </div>
            {unassignedLocations.length > 0 && (
                <div className="regions-unassigned">
                    <p>
                        <strong>{unassignedLocations.length} location
                        {unassignedLocations.length !== 1 ? 's' : ''}</strong> are not linked to a region
                        (missing or unknown regional division). They won&apos;t appear under Region cards until
                        reassigned.
                    </p>
                    <ul>
                        {unassignedLocations.map((loc) => (
                            <li key={loc.id}>
                                <button type="button" onClick={() => setLocationModal({ open: true, location: loc })}>
                                    {loc.name}
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            {/* Region List */}
            <div className="regions-list">
                {filteredData.map((region) => (
                    <RegionCard
                        key={region.id}
                        region={region}
                        isExpanded={expandedRegions.has(region.id)}
                        onToggle={() => toggleRegion(region.id)}
                        onEditRegion={() => setRegionModal({ open: true, region })}
                        onDeleteRegion={() => setDeleteRegionModal({ open: true, region })}
                        onAddDistrict={() =>
                            setDistrictModal({ open: true, regionId: region.id, regionName: region.name })
                        }
                        onEditDistrict={(district) =>
                            setDistrictModal({ open: true, regionId: region.id, regionName: region.name, district })
                        }
                        onDeleteDistrict={(district) => setDeleteDistrictModal({ open: true, district })}
                        onEditLocation={(location) => setLocationModal({ open: true, location })}
                        onDeleteLocation={(location) => setDeleteLocationModal({ open: true, location })}
                        onAddLocation={handleCreateLocation}
                        getLocationEquipmentCount={getLocationEquipmentCount}
                    />
                ))}

                {/* Add Another Region */}
                <button className="regions-add-another" onClick={() => setRegionModal({ open: true })}>
                    + Add Another Region
                </button>
            </div>
            {/* Modals */}
            {regionModal.open && (
                <AddRegionModal
                    region={regionModal.region}
                    onClose={() => setRegionModal({ open: false })}
                    onSubmit={handleCreateRegion}
                />
            )}
            {districtModal.open && (
                <AddDistrictModal
                    regionId={districtModal.regionId!}
                    regionName={districtModal.regionName!}
                    district={districtModal.district}
                    onClose={() => setDistrictModal({ open: false })}
                    onSubmit={handleCreateDistrict}
                />
            )}
            {locationModal.open && locationModal.location && (
                <EditLocationModal
                    location={locationModal.location}
                    companyId={companyId}
                    serviceCenterOptions={serviceCenterOptions}
                    serviceCenterId={assignedServiceCenterId(locationModal.location)}
                    onAssignServiceCenter={handleAssignServiceCenter}
                    onServiceCentersChanged={reloadServiceCenters}
                    onClose={() => setLocationModal({ open: false })}
                />
            )}
            {deleteRegionModal.open && deleteRegionModal.region && (
                <DeleteRegionModal
                    region={deleteRegionModal.region}
                    getLocationEquipmentCount={getLocationEquipmentCount}
                    onClose={() => setDeleteRegionModal({ open: false })}
                    onConfirm={() => handleDeleteRegion(deleteRegionModal.region!.id)}
                />
            )}
            {deleteDistrictModal.open && deleteDistrictModal.district && (
                <DeleteDistrictModal
                    district={deleteDistrictModal.district}
                    getLocationEquipmentCount={getLocationEquipmentCount}
                    onClose={() => setDeleteDistrictModal({ open: false })}
                    onConfirm={() => handleDeleteDistrict(deleteDistrictModal.district!.id)}
                />
            )}
            {deleteLocationModal.open && deleteLocationModal.location && (
                <DeleteLocationModal
                    location={deleteLocationModal.location}
                    equipmentCount={getLocationEquipmentCount(deleteLocationModal.location.id)}
                    onClose={() => setDeleteLocationModal({ open: false })}
                    onConfirm={() => handleDeleteLocation(deleteLocationModal.location!.id)}
                />
            )}
        </div>
    );
};
